import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "api";
import { generatePDFReport } from "./exports/PDFExport";
import { generateDOCXReport } from "./exports/DOCXExport";
import logo from "assets/images/logo_nova_4d_azul.png";
import "./ReportPreview.css";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = d => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const sevLabel = s => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const sevKey = s => {
  const v = (s || "").toLowerCase();
  if (v === "alta") return "high";
  if (v === "média" || v === "media") return "mid";
  return "low";
};

const pad2 = n => String(n).padStart(2, "0");

const SEVERITY_OPTIONS = ["alta", "média", "baixa"];

const XRESUMO_FIELDS = [
  { key: "parecer",                label: "Parecer da IA"             },
  { key: "sumario_pontos",         label: "Sumário dos Pontos"        },
  { key: "oportunidades_melhoria", label: "Oportunidades de Melhoria" },
  { key: "referencias",            label: "Referências"               },
  { key: "conclusao_recomendacoes",label: "Conclusão e Recomendações" },
];

const COLOR_PRESETS = [
  { hex: "#1B3A5C", label: "Azul escuro"   },
  { hex: "#035184", label: "Azul marca"    },
  { hex: "#0B5E42", label: "Verde técnico" },
  { hex: "#7C2D3E", label: "Bordô"         },
  { hex: "#2D3748", label: "Grafite"       },
  { hex: "#C4963A", label: "Dourado"       },
];

// ── EditableField ─────────────────────────────────────────────────────────────

const EF = ({ value, onChange, multiline, label, placeholder, className }) => {
  const [on, setOn] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (on && ref.current) ref.current.focus(); }, [on]);

  return (
    <div className={`ef ${on ? "ef--on" : ""} ${className || ""}`}>
      {label && <span className="ef__lbl">{label}</span>}
      {on ? (
        multiline
          ? <textarea ref={ref} className="ef__input ef__input--ta" value={value || ""} onChange={e => onChange(e.target.value)} onBlur={() => setOn(false)} />
          : <input    ref={ref} className="ef__input"               value={value || ""} onChange={e => onChange(e.target.value)} onBlur={() => setOn(false)} type="text" />
      ) : (
        <div className="ef__show" onClick={() => setOn(true)}>
          {value ? <span className="ef__val">{value}</span> : <span className="ef__ph">{placeholder || "Clique para editar…"}</span>}
          <span className="ef__pencil">✎</span>
        </div>
      )}
    </div>
  );
};

// ── OccurrencePage ────────────────────────────────────────────────────────────

const OccurrencePage = ({ pair, index, pageNum, totalPages, onUpdate, onImageReplace, onRemove, innerRef }) => {
  const imgRef = useRef(null);
  return (
    <div className="rp-page rp-page--occ" ref={innerRef} id={`pg-occ-${index}`}>
      <header className="rp-page__hd">
        <span className="rp-page__tag">Ocorrência</span>
        <div className="rp-page__rule" />
        <button className="rp-page__del" onClick={onRemove} title="Remover página">
          <i className="fas fa-trash-alt" />
        </button>
      </header>

      <div className="occ__top">
        <span className="occ__num">{pad2(index + 1)}</span>
        <EF value={pair.title} onChange={v => onUpdate("title", v)} placeholder="Título da ocorrência" className="occ__title" />
        <div className="occ__sev-wrap">
          <span className="ef__lbl">Severidade</span>
          <select
            className={`occ__sev occ__sev--${sevKey(pair.severity)}`}
            value={(pair.severity || "baixa").toLowerCase()}
            onChange={e => onUpdate("severity", e.target.value)}
          >
            {SEVERITY_OPTIONS.map(o => <option key={o} value={o}>{sevLabel(o)}</option>)}
          </select>
        </div>
      </div>

      <div className="occ__body">
        <div className="occ__img-col">
          {pair.imageUrl
            ? <img src={pair.imageUrl} alt={`Ocorrência ${index + 1}`} className="occ__img" />
            : <div className="occ__img-empty"><i className="fas fa-image" /><span>Sem imagem</span></div>
          }
          <button className="occ__img-btn" onClick={() => imgRef.current?.click()}>
            <i className="fas fa-upload" /> Substituir
          </button>
          <input ref={imgRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => e.target.files[0] && onImageReplace(e.target.files[0])} />
        </div>

        <div className="occ__text-col">
          <EF value={pair.description} onChange={v => onUpdate("description", v)} label="Descrição" multiline placeholder="Descreva a ocorrência…" className="occ__desc" />
          <EF value={pair.action}      onChange={v => onUpdate("action", v)}      label="Ação Recomendada" multiline placeholder="Descreva a ação recomendada…" className="occ__action" />
        </div>
      </div>

      <footer className="rp-page__ft">
        <span className="rp-page__pgn">◇ {pad2(pageNum)} <span className="rp-page__pgtotal">/ {pad2(totalPages)}</span></span>
      </footer>
    </div>
  );
};

// ── ReportPreview ─────────────────────────────────────────────────────────────

const ReportPreview = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const token     = useSelector(s => s.auth.accessToken);

  const {
    filePairs: init = [],
    project = {},
    date,
    xResumoData: initXR = null,
    annotatedImagesUrls: initAnn = [],
    format = "pdf",
  } = location.state || {};

  const [pairs,     setPairs]     = useState(() => init.map(p => ({ ...p })));
  const [xresumo,   setXresumo]   = useState(initXR ? { ...initXR } : null);
  const [logoSrc,   setLogoSrc]   = useState(null);
  const [freePlan,  setFreePlan]  = useState(true);
  const [planReady, setPlanReady] = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [activePg,     setActivePg]     = useState("cover");
  const [accentColor,  setAccentColor]  = useState("#1B3A5C");
  const [orientation,  setOrientation]  = useState("portrait");

  const logoRef   = useRef(null);
  const canvasRef = useRef(null);
  const pageRefs  = useRef({});

  // fetch plan + trial (limitações de “trial” nos relatórios)
  useEffect(() => {
    (async () => {
      try {
        const headers = { headers: { Authorization: `Bearer ${token}` } };
        const ur = await api.get("/users/logged", headers);
        const sub = ur.data?.subscription_status;
        const paid = ["active", "trialing", "pending_cancel"].includes(sub);
        const te = ur.data?.trial_ends_at;
        const inTrial = Boolean(te && new Date(te) > new Date() && !paid);
        setFreePlan(inTrial);
      } catch {
        setFreePlan(true);
      } finally {
        setPlanReady(true);
      }
    })();
  }, [token]);

  // page list (derived)
  const pages = useMemo(() => [
    { id: "cover",     type: "cover",     label: "Capa",             icon: "fas fa-file-alt"    },
    ...pairs.map((_, i) => ({ id: `occ-${i}`, type: "occ", label: `Item ${pad2(i + 1)}`, icon: "fas fa-map-marker-alt", idx: i })),
    ...(xresumo         ? [{ id: "xresumo",   type: "xresumo",   label: "Resumo Executivo", icon: "fas fa-chart-bar"   }] : []),
    ...(initAnn.length  ? [{ id: "annotated", type: "annotated", label: "Imagens Anotadas", icon: "fas fa-images"      }] : []),
  ], [pairs.length, xresumo, initAnn.length]); // eslint-disable-line

  const totalPages = pages.length;

  // scroll tracking
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ids = pages.map(p => p.id);
    const handler = () => {
      const top = el.scrollTop + 140;
      for (let i = ids.length - 1; i >= 0; i--) {
        const ref = pageRefs.current[ids[i]];
        if (ref && ref.offsetTop <= top) { setActivePg(ids[i]); break; }
      }
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [pages]);

  const scrollTo = id => {
    const el = pageRefs.current[id];
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setActivePg(id); }
  };

  // mutations
  const addPage = () => {
    const newIdx = pairs.length;
    setPairs(prev => [...prev, { imageUrl: "", jsonName: `manual_${Date.now()}.json`, title: "Nova Ocorrência", description: "", action: "", severity: "baixa", date }]);
    setTimeout(() => scrollTo(`occ-${newIdx}`), 80);
  };

  const removePage = idx => setPairs(prev => prev.filter((_, i) => i !== idx));

  const updatePair = (idx, key, val) =>
    setPairs(prev => { const n = [...prev]; n[idx] = { ...n[idx], [key]: val }; return n; });

  const replaceImg = (idx, file) => {
    const r = new FileReader();
    r.onload = e => updatePair(idx, "imageUrl", e.target.result);
    r.readAsDataURL(file);
  };

  const changeLogo = file => {
    const r = new FileReader();
    r.onload = e => setLogoSrc(e.target.result);
    r.readAsDataURL(file);
  };

  const updateXR = (key, val) => setXresumo(prev => ({ ...(prev || {}), [key]: val }));

  const doExport = async () => {
    setExporting(true);
    try {
      const fn = format === "pdf" ? generatePDFReport : generateDOCXReport;
      await fn(pairs, project, date, xresumo, initAnn, logoSrc, orientation);
    } finally { setExporting(false); }
  };

  const currentLogo = logoSrc || logo;

  // page number helper (cover = 1, occs start at 2, etc.)
  const pgNum = id => {
    const i = pages.findIndex(p => p.id === id);
    return i + 1;
  };

  return (
    <div className={`rp${orientation === "landscape" ? " rp--land" : ""}`} style={{ "--rp-accent": accentColor }}>

      {/* ── LEFT: Page Navigator ─────────────────────────── */}
      <nav className="rp-nav">
        <div className="rp-nav__brand">
          <span className="rp-nav__brand-line" />
          <span className="rp-nav__brand-text">PREVIEW</span>
          <span className="rp-nav__brand-line" />
        </div>

        <ul className="rp-nav__list">
          {pages.map(pg => (
            <li key={pg.id} className={`rp-nav__item rp-nav__item--${pg.type} ${activePg === pg.id ? "rp-nav__item--active" : ""}`}>
              <button className="rp-nav__btn" onClick={() => scrollTo(pg.id)}>
                <i className={`${pg.icon} rp-nav__ico`} />
                <span className="rp-nav__lbl">{pg.label}</span>
              </button>
              {pg.type === "occ" && (
                <button className="rp-nav__rm" onClick={() => removePage(pg.idx)} title="Remover">
                  <i className="fas fa-times" />
                </button>
              )}
            </li>
          ))}
        </ul>

        <button className="rp-nav__add" onClick={addPage}>
          <i className="fas fa-plus" /> <span>Nova página</span>
        </button>

        <div className="rp-nav__foot">
          <span className="rp-nav__count">{totalPages} pág.</span>
        </div>
      </nav>

      {/* ── CENTER: Document Canvas ───────────────────────── */}
      <main className="rp-canvas" ref={canvasRef}>
        <div className="rp-pages">

          {/* COVER */}
          <div className="rp-page rp-page--cover" ref={el => pageRefs.current["cover"] = el} id="pg-cover">
            <header className="rp-page__hd">
              <div className="rp-page__rule" />
            </header>
            <div className="cover">
              <img src={currentLogo} alt="Logo" className="cover__logo" />
              <div className="cover__bar" />
              <h1 className="cover__title">Relatório de<br />Inspeção Técnica</h1>
              <dl className="cover__meta">
                {[
                  ["Projeto",     project?.name],
                  ["Tipo",        project?.type],
                  ["Data",        fmtDate(date)],
                  ["Ocorrências", pairs.length],
                ].map(([k, v]) => (
                  <div key={k} className="cover__row">
                    <dt className="cover__key">{k}</dt>
                    <dd className="cover__val">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <footer className="rp-page__ft">
              <span className="rp-page__pgn">◇ 01 <span className="rp-page__pgtotal">/ {pad2(totalPages)}</span></span>
            </footer>
          </div>

          {/* OCCURRENCES */}
          {pairs.map((pair, idx) => (
            <OccurrencePage
              key={`occ-${idx}`}
              pair={pair}
              index={idx}
              pageNum={pgNum(`occ-${idx}`)}
              totalPages={totalPages}
              onUpdate={(k, v) => updatePair(idx, k, v)}
              onImageReplace={f => replaceImg(idx, f)}
              onRemove={() => removePage(idx)}
              innerRef={el => pageRefs.current[`occ-${idx}`] = el}
            />
          ))}

          {pairs.length === 0 && (
            <div className="rp-empty-state">
              <i className="fas fa-file-circle-plus" />
              <p>Nenhuma ocorrência.<br />Use "Nova página" para adicionar.</p>
            </div>
          )}

          {/* EXECUTIVE SUMMARY */}
          {xresumo && (
            <div className="rp-page rp-page--xresumo" ref={el => pageRefs.current["xresumo"] = el} id="pg-xresumo">
              <header className="rp-page__hd">
                <span className="rp-page__tag">Resumo Executivo</span>
                <div className="rp-page__rule" />
              </header>
              <div className="xr">
                {XRESUMO_FIELDS.map(({ key, label }) => {
                  const raw = xresumo[key];
                  if (!raw) return null;
                  const val = Array.isArray(raw) ? raw.join("\n\n") : raw;
                  return (
                    <div key={key} className="xr__block">
                      <EF value={val} onChange={v => updateXR(key, v)} label={label} multiline className="xr__ef" />
                    </div>
                  );
                })}
              </div>
              <footer className="rp-page__ft">
                <span className="rp-page__pgn">◇ {pad2(pgNum("xresumo"))} <span className="rp-page__pgtotal">/ {pad2(totalPages)}</span></span>
              </footer>
            </div>
          )}

          {/* ANNOTATED IMAGES */}
          {initAnn.length > 0 && (
            <div className="rp-page rp-page--ann" ref={el => pageRefs.current["annotated"] = el} id="pg-ann">
              <header className="rp-page__hd">
                <span className="rp-page__tag">Imagens Anotadas</span>
                <div className="rp-page__rule" />
              </header>
              <div className="ann">
                {initAnn.map((url, i) => (
                  <div key={i} className="ann__item">
                    <span className="ann__lbl">Fig. {pad2(i + 1)}</span>
                    <img src={url} alt={`Anotada ${i + 1}`} className="ann__img" />
                  </div>
                ))}
              </div>
              <footer className="rp-page__ft">
                <span className="rp-page__pgn">◇ {pad2(pgNum("annotated"))} <span className="rp-page__pgtotal">/ {pad2(totalPages)}</span></span>
              </footer>
            </div>
          )}

        </div>
      </main>

      {/* ── RIGHT: Controls Panel ─────────────────────────── */}
      <aside className="rp-panel">
        <div className="rp-panel__in">

          {/* ── Personalização (plano pago) ────────────────── */}
          <div className="rp-panel__custom-sec">
            <div className="rp-panel__logo-sec">
              <img src={currentLogo} alt="Logo" className="rp-panel__logo" />
            </div>

            {planReady && (
              freePlan ? (
                <div className="rp-panel__upgrade">
                  <i className="fas fa-lock rp-panel__upgrade-icon" />
                  <span className="rp-panel__upgrade-title">Personalização</span>
                  <span className="rp-panel__upgrade-desc">
                    Troca de logo e cores disponíveis nos planos pagos.
                  </span>
                  <span className="rp-panel__upgrade-badge">Plano pago</span>
                </div>
              ) : (
                <>
                  {/* Logo swap */}
                  <button className="rp-panel__logo-btn" onClick={() => logoRef.current?.click()}>
                    <i className="fas fa-exchange-alt" /> Trocar logo
                  </button>
                  <input ref={logoRef} type="file" accept="image/*" style={{ display: "none" }}
                    onChange={e => e.target.files[0] && changeLogo(e.target.files[0])} />

                  {/* Color picker */}
                  <div className="rp-panel__color">
                    <span className="rp-panel__mini-lbl">Cor das Linhas</span>
                    <div className="rp-panel__swatches">
                      {COLOR_PRESETS.map(({ hex, label }) => (
                        <button
                          key={hex}
                          className={`rp-swatch${accentColor === hex ? " rp-swatch--on" : ""}`}
                          style={{ background: hex }}
                          onClick={() => setAccentColor(hex)}
                          title={label}
                        />
                      ))}
                      <label className="rp-swatch rp-swatch--pick" title="Cor personalizada">
                        <input
                          type="color"
                          value={accentColor}
                          onChange={e => setAccentColor(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </>
              )
            )}
          </div>

          {/* Orientation */}
          <div className="rp-panel__orient">
            <span className="rp-panel__mini-lbl">Orientação</span>
            <div className="rp-panel__orient-row">
              {[
                { val: "portrait",  icon: "fas fa-file",  label: "Retrato"  },
                { val: "landscape", icon: "fas fa-file",  label: "Paisagem" },
              ].map(({ val, icon, label }) => (
                <button
                  key={val}
                  className={`rp-orient${orientation === val ? " rp-orient--on" : ""}`}
                  onClick={() => setOrientation(val)}
                >
                  <i className={`${icon}${val === "landscape" ? " rp-orient__ico-rot" : ""}`} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rp-panel__sep" />

          <div className="rp-panel__fmt">
            <span className={`rp-panel__fmt-badge rp-panel__fmt-badge--${format}`}>
              <i className={`fas ${format === "pdf" ? "fa-file-pdf" : "fa-file-word"}`} />
              {format.toUpperCase()}
            </span>
          </div>

          <div className="rp-panel__stats">
            <div className="rp-panel__stat">
              <span className="rp-panel__stat-n">{pairs.length}</span>
              <span className="rp-panel__stat-l">ocorrências</span>
            </div>
            <div className="rp-panel__stat">
              <span className="rp-panel__stat-n">{totalPages}</span>
              <span className="rp-panel__stat-l">páginas</span>
            </div>
          </div>

          <div className="rp-panel__sep" />

          <button className="rp-panel__export" onClick={doExport} disabled={exporting}>
            {exporting
              ? <><span className="rp-spin" /> Gerando…</>
              : <><i className="fas fa-download" /> Exportar {format.toUpperCase()}</>
            }
          </button>

          <button className="rp-panel__add" onClick={addPage}>
            <i className="fas fa-plus" /> Nova página
          </button>

          <button className="rp-panel__back" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" /> Voltar
          </button>

          <p className="rp-panel__hint">Clique em qualquer campo para editar. Alterações apenas nesta exportação.</p>
        </div>
      </aside>

    </div>
  );
};

export default ReportPreview;
